import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'
import { AppRoute, TasksKanbanRelatedToType } from '@shared/config'
import { useToast } from '@shared/ui/toast'

import { useCustomerBehalf } from '../api/list/behalf.hook'
import { useCustomerPrivileges } from './customer-privileges.hook'
import { useCustomerRefresh } from './customer-refresh.hook'
import { useCustomerCrmPullApi } from '../api/actions.hook'
import { useCustomerCrmPushApi } from '../api/actions.hook'
import { useCustomerSendReloadApi } from '../api/actions.hook'
import { useCustomerRespondPushApi } from '../api/respond-push.hook'

interface CustomerActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  isProcessing: ComputedRef<boolean>
  isVisible: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  canAddKid: ComputedRef<boolean>
  canDelete: ComputedRef<boolean>
  isTasksVisible: ComputedRef<boolean>
  toggle: () => void
  actOnBehalf: () => Promise<void>
  addKid: () => void
  createTask: () => Promise<void>
  viewTasks: () => Promise<void>
  pushToCrm: () => Promise<void>
  pullFromCrm: () => Promise<void>
  askDelete: () => void
  sendReload: () => Promise<void>
  pushToRespond: () => Promise<void>
}

/** The customer page kebab: the legacy DropdownGeneral, each item gated on its endpoint's permission. */
export const useCustomerActions = (): CustomerActionsReturn => {
  const route = useRoute()
  const toast = useToast()
  const customerStore = useCustomerStore()
  const listsStore = useListsStore()
  const { runWithPageLoader, refreshCustomer } = useCustomerRefresh()
  const {
    canManageCustomers,
    canManageKids,
    canDeleteCustomers,
  } = useCustomerPrivileges()

  const customerId = Number(route.params.id)

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const isProcessing = computed<boolean>(() => customerStore.isProcessing)
  const isTasksVisible = computed<boolean>(() =>
    !!listsStore.getLists?.tasks_kanban?.enabled && customerStore.getDetails?.status === CustomerStatus.Deal)
  const isVisible = computed<boolean>(() =>
    canManageCustomers.value || canManageKids.value || canDeleteCustomers.value || isTasksVisible.value)

  const close = (): void => {
    isOpen.value = false
  }

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  /** The link arrives from a POST, so the tab is opened inside the click (popup blockers) and pointed at it later. */
  const actOnBehalf = async (): Promise<void> => {
    close()

    const popup = window.open('', '_blank')
    const url = await useCustomerBehalf(customerId)

    if (!popup) return

    if (!url) {
      popup.close()
      return
    }

    popup.opener = null
    popup.location.href = url
  }

  const addKid = (): void => {
    close()
    customerStore.setModalKidCreate(true)
  }

  const createTask = async (): Promise<void> => {
    close()
    await navigateTo({
      path: AppRoute.TasksKanbanCreation,
      query: {
        customer_id: String(customerId),
        related_to_id: String(customerId),
        related_to_type: TasksKanbanRelatedToType.Customer,
      },
    })
  }

  const viewTasks = async (): Promise<void> => {
    close()
    await navigateTo({
      path: AppRoute.TasksKanbanSupport,
      query: { 'customer_id[0]': String(customerId) },
    })
  }

  const runAndRefetch = async (action: (id: number) => Promise<boolean>): Promise<void> => {
    close()

    await runWithPageLoader(async () => {
      if (await action(customerId)) {
        await refreshCustomer(customerId)
      }
    })
  }

  const pushToCrm = (): Promise<void> => runAndRefetch(useCustomerCrmPushApi)
  const pullFromCrm = (): Promise<void> => runAndRefetch(useCustomerCrmPullApi)

  const askDelete = (): void => {
    close()
    customerStore.setModalDelete(true)
  }

  const sendReload = async (): Promise<void> => {
    close()
    await useCustomerSendReloadApi(customerId)
  }

  const pushToRespond = async (): Promise<void> => {
    close()

    await runWithPageLoader(async () => {
      if (await useCustomerRespondPushApi(customerId)) {
        toast.success({ title: 'Success!', description: 'Success sync' })
        await refreshCustomer(customerId)
      }
    })
  }

  onClickOutside(containerRef, close)

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    isProcessing,
    isVisible,
    canManage: canManageCustomers,
    canAddKid: canManageKids,
    canDelete: canDeleteCustomers,
    isTasksVisible,
    toggle,
    actOnBehalf,
    addKid,
    createTask,
    viewTasks,
    pushToCrm,
    pullFromCrm,
    askDelete,
    sendReload,
    pushToRespond,
  }
}
